import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import { selectCurrentUser } from '../features/auth/authSlice';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import type { QmsRegistryKind, QmsStatistics } from '../features/qms/qmsApi';
import { fetchQmsStatistics, listQmsRecords } from '../features/qms/qmsApi';
import { permissionKeyForKind } from '../features/qms/permissionKeys';
import {
  deriveCorrectiveAxis,
  deriveExpedienteAxis,
  labelQmsAttributability,
  labelQmsOrigin,
  labelQmsRecordStatus,
  labelQmsReview,
  labelTypificationMode,
  qmsRecordStatusBadgeClass,
  qmsReviewBadgeClass,
} from '../features/qms/qmsLabels';
import { DataTable } from '../components/DataTable';
import { DateCell } from '../components/DateCell';
import { QmsRecordActionsDropdown } from '../components/QmsRecordActionsDropdown';
import { Link } from 'react-router-dom';

export type QmsListRow = {
  id: string;
  status: string;
  incidentDate: string;
  originType: string;
  attributability: string;
  typificationMode: string;
  description: string;
  createdAt: string;
  reviewOutcome: string;
  correctiveWaived?: boolean;
  internalTriggersCorrective?: boolean;
};

const columnHelper = createColumnHelper<QmsListRow>();

interface Props {
  kind: QmsRegistryKind;
  title: string;
  basePath: string;
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col items-center justify-center min-w-[120px]">
      <span className={`text-2xl font-bold ${accent ?? 'text-foreground'}`}>{value}</span>
      <span className="text-xs text-muted-foreground mt-1 text-center">{label}</span>
    </div>
  );
}

function DashboardPanel({ stats }: { stats: QmsStatistics }) {
  const sortedTyp = useMemo(
    () => [...stats.byTypification].sort((a, b) => b.count - a.count),
    [stats.byTypification]
  );
  const maxTypCount = sortedTyp.length > 0 ? sortedTyp[0].count : 1;

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-foreground">Tablero de estadísticas</h2>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total" value={stats.total} />
        <StatCard label="Abiertas" value={stats.openCount} accent="text-amber-600 dark:text-amber-400" />
        <StatCard label="Cerradas" value={stats.closedCount} accent="text-green-600 dark:text-green-400" />
        <StatCard
          label="Desestimadas"
          value={stats.byStatus.find((s) => s.status === 'dismissed')?.count ?? 0}
          accent="text-red-600 dark:text-red-400"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* By status */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground">Por estado</h3>
          <ul className="text-sm space-y-1">
            {stats.byStatus.map((s) => (
              <li key={s.status} className="flex items-center justify-between gap-2">
                <span className={qmsRecordStatusBadgeClass(s.status)}>
                  {labelQmsRecordStatus(s.status)}
                </span>
                <span className="font-semibold tabular-nums">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* By origin */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground">Reclamos externos vs. desvíos internos</h3>
          <ul className="text-sm space-y-1">
            {stats.byOrigin.map((o) => (
              <li key={o.origin} className="flex items-center justify-between gap-2">
                <span>{labelQmsOrigin(o.origin)}</span>
                <span className="font-semibold tabular-nums">{o.count}</span>
              </li>
            ))}
          </ul>
          {stats.byAttributability.length > 0 && (
            <>
              <h3 className="text-xs font-medium text-muted-foreground pt-2">Por atribuibilidad</h3>
              <ul className="text-sm space-y-1">
                {stats.byAttributability.map((a) => (
                  <li key={a.attributability} className="flex items-center justify-between gap-2">
                    <span>{labelQmsAttributability(a.attributability)}</span>
                    <span className="font-semibold tabular-nums">{a.count}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* By typification (areas/processes) */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground">Áreas / procesos con más NC</h3>
          {sortedTyp.length === 0 ? (
            <p className="text-xs text-muted-foreground">Sin datos de tipificación</p>
          ) : (
            <ul className="text-sm space-y-2">
              {sortedTyp.slice(0, 10).map((t, idx) => {
                const label = t.itemLabel ?? labelTypificationMode(t.mode);
                const pct = Math.round((t.count / maxTypCount) * 100);
                return (
                  <li key={`${t.mode}-${t.itemId ?? idx}`} className="space-y-0.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate" title={label}>{label}</span>
                      <span className="font-semibold tabular-nums shrink-0">{t.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export function QmsRecordsListPage({ kind, title, basePath }: Props) {
  const { canEdit, canView, ready } = usePlatformAccessLevel(permissionKeyForKind(kind));
  const user = useSelector(selectCurrentUser);
  const [rows, setRows] = useState<QmsListRow[]>([]);
  const [stats, setStats] = useState<QmsStatistics | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      const res = await listQmsRecords(kind, 1, 50);
      setRows(res.data as QmsListRow[]);
      if (canEdit && ready) {
        const s = await fetchQmsStatistics(kind);
        setStats(s);
      } else {
        setStats(null);
      }
      setError('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  }, [kind, canEdit, ready]);

  useEffect(() => {
    reload();
  }, [reload]);

  const actionsRenderer = useCallback(
    (info: { row: { original: QmsListRow } }) => (
      <QmsRecordActionsDropdown recordId={info.row.original.id} basePath={basePath} canManage={canEdit && ready} />
    ),
    [basePath, canEdit, ready]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('incidentDate', {
        header: 'Fecha incidente',
        cell: (info) => <DateCell value={info.getValue()} />,
      }),
      columnHelper.accessor('description', {
        header: 'Descripción',
        cell: (info) => {
          const text = info.getValue() ?? '';
          return (
            <span className="block max-w-[360px] whitespace-normal break-words" title={text}>
              {text}
            </span>
          );
        },
      }),
      columnHelper.accessor('originType', {
        header: 'Origen',
        cell: (info) => labelQmsOrigin(info.getValue()),
      }),
      columnHelper.accessor('typificationMode', {
        header: 'Tipificación',
        cell: (info) => labelTypificationMode(info.getValue()),
      }),
      columnHelper.accessor('attributability', {
        header: 'Atribuibilidad',
        cell: (info) => labelQmsAttributability(info.getValue()),
      }),
      columnHelper.display({
        id: 'expedienteAxis',
        header: 'Expediente',
        cell: (info) => {
          const axis = deriveExpedienteAxis(info.row.original);
          return <span className={axis.className}>{axis.label}</span>;
        },
      }),
      columnHelper.display({
        id: 'correctiveAxis',
        header: 'Acción correctiva',
        cell: (info) => {
          const axis = deriveCorrectiveAxis(info.row.original);
          return <span className={axis.className}>{axis.label}</span>;
        },
      }),
      columnHelper.accessor('reviewOutcome', {
        header: 'Revisión',
        cell: (info) => (
          <span className={qmsReviewBadgeClass(info.getValue())}>{labelQmsReview(info.getValue())}</span>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'Alta',
        cell: (info) => <DateCell value={info.getValue()} />,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Acciones',
        cell: actionsRenderer,
      }),
    ],
    [actionsRenderer]
  );

  const isAdminUi = user?.role === 'superadmin' || user?.role === 'admin';

  return (
    <div className="space-y-6 py-8 px-4 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        <div className="flex gap-2">
          {canView && ready && (
            <Link
              to={`${basePath}/nueva`}
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Registrar
            </Link>
          )}
          {canEdit && ready && (
            <Link
              to={`${basePath}/reportes`}
              className="inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
            >
              Reportes
            </Link>
          )}
          {isAdminUi && (
            <Link
              to={`${basePath}/plantilla`}
              className="inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
            >
              Campos dinámicos
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">{error}</div>
      )}

      {canEdit && stats && <DashboardPanel stats={stats} />}

      <DataTable
        columns={columns}
        data={rows}
        emptyMessage="No hay registros"
        pageSizeOptions={[10, 20, 50]}
      />
    </div>
  );
}
